import React, { useEffect, useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import axios from 'axios'
import { BASE_URL } from '../../utils/api'
import Spinner from '../Spinner'
import { toast } from 'react-toastify'
import {
  formatHarvestEstimatedValue,
  formatHarvestPriceDisplay,
  formatHarvestQuantityDisplay,
  getWeightFieldLabel,
} from '../../utils/harvestRequest'

const HarvestDetailDrawer = ({
  showDetailDrawer,
  setShowDetailDrawer,
  onClose,
  selected = {},
  onSubmit
}) => {
  // Example status badge logic
  // Use detail for status if available, else fallback
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [respondLoading, setRespondLoading] = useState(false)
  const [drawerWidth, setDrawerWidth] = useState(600)

  useEffect(() => {
    if (showDetailDrawer && selected?.id) {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      axios.get(`${BASE_URL}harvest-requests/${selected.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
        .then(res => {
          setDetail(res.data)
          setLoading(false)
        })
        .catch(err => {
          setError(err)
          setLoading(false)
        })
    } else {
      setDetail(null)
    }
  }, [showDetailDrawer, selected?.id])

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 700) {
        setDrawerWidth(window.innerWidth - 20) // 20px margin
      } else {
        setDrawerWidth(600)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize() // set initial size
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const status = detail?.request_status || selected?.status || 'Responded'
  const statusColor = status === 'Responded' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'

  // Mark as responded handler
  const handleMarkAsResponded = async () => {
    if (!detail?.id) return
    setRespondLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    try {
      await axios.patch(`${BASE_URL}harvest-requests/${detail.id}/mark-as-read/`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      // Refetch detail after marking as responded
      const res = await axios.get(`${BASE_URL}harvest-requests/${detail.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      setDetail(res.data)
      toast.success('Harvest request marked as read successfully')
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Request failed. Please try again')
    } finally {
      setRespondLoading(false)
    }
  }

  return (
    <Drawer
      open={showDetailDrawer}
      onClose={() => setShowDetailDrawer(false)}
      direction="right"
      size={drawerWidth}
    >
      <div className='w-full h-full overflow-y-auto dark:bg-dark'>
        <div className='w-full h-full'>
          {/* Header */}
          <div className='p-4 w-full flex flex-row items-center justify-between border-b border-gray-200 dark:border-border-dark mb-4'>
            <h1 className='text-xl font-bold dark:text-white'>Request details</h1>

            <button
              onClick={() => setShowDetailDrawer(false)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <AiOutlineCloseCircle className="h-7 w-7 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
            </button>
          </div>

          {/* Loading/Error */}
          {loading ? (
            <div className="flex justify-center items-center h-40"><Spinner /></div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">Failed to load details.</div>
          ) : (
            <>
              {/* Status */}
              <div className="border-b px-6 pb-2 flex flex-row items-center justify-between gap-6 dark:border-border-dark">
                <div className="flex flex-row items-center gap-6">
                  <span className="text-yellowcolor font-medium text-sm">Request Status</span>
                  <span className={`capitalize inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusColor}`}>
                    <span className="w-2 h-2  rounded-full bg-green-500 mr-2 inline-block"></span>
                    {status}
                  </span>
                </div>

                {status?.toLowerCase() === 'pending' && (
                  <button
                    className="bg-green-600 text-sm hover:bg-green-700 text-white font-normal px-6 py-1.5 rounded-md ml-4 disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={handleMarkAsResponded}
                    disabled={respondLoading}
                  >
                    {respondLoading ? "Please wait..." : 'Mark as responded'}
                  </button>
                )}
              </div>

              {/* Request Info Card */}
              <div className="bg-white border rounded-xl mt-6 mx-6 p-6 flex flex-col gap-2 dark:bg-dark dark:border-border-dark">
                <div className="flex flex-col flex-wrap">
                  <div className="flex flex-row items-center gap-5 min-w-[160px] mb-2">
                    <span className="text-gray-400 text-sm">Request ID:</span>
                    <span className="font-medium text-base dark:text-white">{detail?.request_id || selected?.request_id || '12324234'}</span>
                  </div>
                  <div className="flex flex-row items-center gap-5 min-w-[160px] mb-2">
                    <span className="text-gray-400 text-sm">Created By:</span>
                    <span className="font-medium text-base dark:text-white">{detail?.owner ? `${detail.owner.firstname} ${detail.owner.lastname}` : selected?.created_by || 'Myself'}</span>
                  </div>
                  <div className="flex flex-row items-center gap-5 min-w-[160px] mb-2">
                    <span className="text-gray-400 text-sm">Created on:</span>
                    <span className="font-medium text-base dark:text-white">{detail?.created_at ? new Date(detail.created_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : selected?.created_at ? new Date(selected.created_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Tues 3 Mar, 2025'}</span>
                  </div>
                </div>
              </div>

              {/* Request Information Card */}
              <div className="bg-white border rounded-xl mt-6 mx-6 p-6 flex flex-col gap-2 dark:bg-dark dark:border-border-dark">
                <span className="font-medium text-lg mb-2 dark:text-white">Request Information</span>
                <div className="flex flex-col gap-6">
                  <div>
                    <span className="text-gray-400 text-sm">Title</span><br />
                    <span className="font-medium text-base dark:text-white">{detail?.title || selected?.title || 'No title'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">{getWeightFieldLabel(detail || selected)}</span><br />
                    <span className="font-medium text-base dark:text-white">{formatHarvestQuantityDisplay(detail || selected)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Price / Unit</span><br />
                    <span className="font-medium text-base dark:text-white">{formatHarvestPriceDisplay(detail || selected)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Est. Total Value</span><br />
                    <span className="font-medium text-base dark:text-white">{formatHarvestEstimatedValue(detail || selected)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Commodity</span><br />
                    <span className="font-medium text-base dark:text-white">{detail?.commodity?.name || selected?.commodity?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Request Type</span><br />
                    <span className="font-medium text-base dark:text-white">
                      {(detail?.to_all_farmer ?? selected?.to_all_farmer) ? 'All Farmers' : 'Group Specific'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default HarvestDetailDrawer